import { useEffect } from 'react';

import { useMutation } from '@tanstack/react-query';

import { useEnsureCsrfToken } from '~/context/CsrfContext';
import {
  acknowledgeHash,
  adoptionStorageKey,
  fetchPushRequest,
  getCommunityPushRegistration,
  hasCommunityManifest,
  isStandaloneCommunity,
  readAcknowledgedHash,
  setCommunityPushContext,
  withCommunityPushLock,
} from '~/utils/communityAppAdoption';
import { NectarPageBlock } from '~/utils/nectarPageBlock';
import { getOrCreateAnonymousUserId } from '~/utils/nectarTracking';

type Observation = { install_signal?: 'appinstalled' | 'standalone'; push?: PushSubscriptionJSON | null };

export const useCommunityAppObservations = (enabled: boolean, publicationId?: string, memberId?: string) => {
  const ensureCsrfToken = useEnsureCsrfToken();
  const storageKey = publicationId && memberId ? adoptionStorageKey(publicationId, memberId) : undefined;
  const { mutateAsync } = useMutation({
    mutationFn: async (observation: Observation) => {
      if (!storageKey) return;
      const csrfToken = await ensureCsrfToken();
      const response = await fetchPushRequest('/community_app_observations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
        body: JSON.stringify({
          ...observation,
          previous_hash: readAcknowledgedHash(storageKey),
          nectar_anonymous_user_id: getOrCreateAnonymousUserId(),
          nectar_is_pwa: isStandaloneCommunity(),
          nectar_page_block: observation.install_signal
            ? NectarPageBlock.CommunityAppInstallObservation
            : NectarPageBlock.CommunityAppBackgroundObservation,
        }),
      });
      if (!response.ok) throw new Error('Observation failed');
      const result = await response.json();
      if (
        result.tracking_enabled &&
        'push' in observation &&
        (result.data_hash === null || typeof result.data_hash === 'string')
      ) {
        acknowledgeHash(storageKey, typeof result.data_hash === 'string' ? result.data_hash : null);
      }
    },
  });

  useEffect(() => {
    if (!storageKey) return;
    let cancelled = false;
    void ensureCsrfToken().then((csrfToken) => {
      if (cancelled) return;
      setCommunityPushContext({
        storageKey,
        csrfToken,
        trackingEnabled: enabled,
        pageBlock: NectarPageBlock.CommunityAppPushPrompt,
      });
    });
    return () => {
      cancelled = true;
      setCommunityPushContext(undefined);
    };
  }, [storageKey, ensureCsrfToken, enabled]);

  useEffect(() => {
    if (!enabled || !storageKey) return;
    let cancelled = false;
    let running = false;
    let pending = false;
    let installed = false;
    let lastSent: string | undefined;

    const observe = async () => {
      if (cancelled) return;
      if (running) {
        pending = true;
        return;
      }
      running = true;
      try {
        await withCommunityPushLock(async () => {
          if (cancelled) return;
          const observation: Observation = {};
          const registration =
            'serviceWorker' in navigator ? await getCommunityPushRegistration().catch(() => null) : null;
          if (hasCommunityManifest()) {
            if (installed) observation.install_signal = 'appinstalled';
            // Only trust standalone mode once the community service worker is registered, so
            // opening Community from another installed PWA (e.g. the publication app) isn't
            // miscounted as a community install.
            else if (registration && isStandaloneCommunity()) observation.install_signal = 'standalone';
          }
          if (registration?.pushManager && 'Notification' in window) {
            try {
              const subscription = await registration.pushManager.getSubscription();
              observation.push = Notification.permission === 'granted' ? (subscription?.toJSON() ?? null) : null;
            } catch {
              // Inspection failures carry no evidence that the registration was removed.
            }
          }
          if (!cancelled && Object.keys(observation).length) {
            // Skip observations identical to the last one so a foregrounded PWA doesn't POST
            // (and take a row lock) on every app switch and screen unlock.
            const key = JSON.stringify(observation);
            if (key !== lastSent) {
              await mutateAsync(observation);
              lastSent = key;
            }
            if (observation.install_signal === 'appinstalled') installed = false;
          }
        });
      } catch {
        // Retry at the next lifecycle opportunity without interrupting the member.
      } finally {
        running = false;
        if (pending && !cancelled) {
          pending = false;
          void observe();
        }
      }
    };
    const onInstalled = () => {
      if (hasCommunityManifest()) installed = true;
      void observe();
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') void observe();
    };
    void observe();
    window.addEventListener('appinstalled', onInstalled);
    document.addEventListener('visibilitychange', onVisible);
    navigator.serviceWorker?.addEventListener('controllerchange', onVisible);
    void navigator.serviceWorker?.ready
      .then(() => {
        if (!cancelled) void observe();
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
      window.removeEventListener('appinstalled', onInstalled);
      document.removeEventListener('visibilitychange', onVisible);
      navigator.serviceWorker?.removeEventListener('controllerchange', onVisible);
    };
  }, [enabled, storageKey, mutateAsync]);
};
